<template>
  <div
    :class="['layouts', { widget: !['alarm-views'].includes(parent) }]"
    style="position: relative"
  >
    <div class="accordion" role="tablist">
      <div
        class="widget-header mx-auto cursor-pointer"
        v-show="isToolbarsCollapsed"
        v-if="parent !== 'alarm-views'"
        @click.prevent="updateToolbarsStatus"
        @mouseover="increaseWidth"
        @mouseleave="resetWidth"
      >
        <img
          :src="require('@/assets/svg/layouts.svg')"
          alt="Tenants"
          :style="{ width: hoverWidth, marginInline: 'auto' }"
          v-b-tooltip.hover.right.viewport="`${$t('grid.widgets.layouts.title')}`"
        />
      </div>

      <div class="widget-header" v-show="!isToolbarsCollapsed">
        <div
          class="icon-title"
          v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
          v-if="!searchBarEnabled && parent !== 'alarm-views'"
        >
          <img
            :src="require('@/assets/svg/layouts.svg')"
            alt="Layouts"
            :style="{ width: '16px', marginInline: '16px' }"
          />
          <p class="text-center">{{ title }}</p>
        </div>

        <div class="search-collapse" :class="[{ barEnable: searchBarEnabled }]">
          <img
            :src="require('@/assets/svg/search.svg')"
            alt="Search"
            :class="{ 'search-icon-config': parent === 'alarm-views' }"
            :style="{ width: '18px', marginInline: '0.65rem' }"
            v-b-tooltip.hover.bottom.viewport="`${$t('search')}`"
            @click.stop="toggleSearchBar(true)"
            v-if="!searchBarEnabled"
          />
          <search-bar-widgets
            :search-bar-width="searchBarWidth"
            :sidebar-min-width-reached="sidebarMinWidthReached"
            :is-config-mode="parent === 'alarm-views'"
            @input="onSearch"
            @toggle-search-bar="toggleSearchBar"
            @reset-search-bar="resetSearchBar"
            v-if="searchBarEnabled"
            ref="layoutsSearchBar"
          />
        </div>
        <template v-if="collapsible">
          <feather-icon
            v-if="!isConfigModeOn && !searchBarEnabled"
            :icon="isCollapseOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
            size="30"
          />
        </template>
        <span class="handle cursor-move" v-if="isConfigModeOn">
          <feather-icon icon="MenuIcon" size="18" />
        </span>
      </div>
      <b-collapse
        :id="`main-accordion-${id}`"
        :accordion="`main-accordion-${id}`"
        role="tabpanel"
        v-model="isCollapseOpen"
        @input="onUpdateStatus"
        v-show="isCollapseOpen && !isToolbarsCollapsed"
      >
        <div
          :class="[
            'layouts-container d-flex flex-wrap justify-content-between',
            { 'flex-column': sidebarMinWidthReached },
          ]"
        >
          <div
            v-for="(layout, index) in items"
            :key="index"
            :class="[
              'layout',
              {
                selected: checkSelectedLayout(layout.id),
                'w-100': sidebarMinWidthReached,
                'w-50': !sidebarMinWidthReached,
                isCard: layout.isCard,
                'layout-card': layout.isCard,
              },
            ]"
            @click="setLayout(layout)"
          >
            <layout-preview
              :key="`${layout.id}-${JSON.stringify(layout.grid || layout.data)}`"
              :layout="layout"
            />
          </div>
        </div>
        <pagination-widgets
          :sidebar-min-width-reached="sidebarMinWidthReached"
          :per-page.sync="perPage"
          :current-page.sync="currentPage"
          :total-records="sourceData.length"
        />
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { BCollapse, VBToggle } from "bootstrap-vue";
import store from "@/store";
import LayoutPreview from "./LayoutPreview";
import PaginationWidgets from "./components/PaginationWidgets";
import SearchBarWidgets from "./components/SearchBarWidgets.vue";

export default {
  components: {
    BCollapse,
  },
  components: {
    LayoutPreview,
    BCollapse,
    PaginationWidgets,
    SearchBarWidgets,
  },
  directives: {
    "b-toggle": VBToggle,
  },
  props: {
    id: {
      type: Number | String,
      default: 1,
    },
    location: {
      type: String,
      default: "sideBar",
    },
    title: {
      type: String | Number,
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
    isOpen: {
      type: Boolean,
      default: true,
    },
    parent: {
      type: String | Number,
      default: "sidebar",
    },
    selectedLayout: {
      type: String | Number,
      default: null,
    },
    forceWideLayout: {
      type: Boolean,
      default: false,
    },
    ignoreCollapse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCollapseOpen: false,
      perPage: 4,
      currentPage: 1,
      sourceData: [],
      searchBarEnabled: false,
      searchBarWidth: 0,
      hoverWidth: "16px",
    };
  },
  mounted() {
    this.isCollapseOpen = this.isOpen;
    store.dispatch("layouts/getUserLayouts").then(() => {
      if (this.parent !== "sidebar") {
        this.$emit("layouts-widget-ready");
      }
      this.sourceData = this.userLayouts;
    });
    this.searchBarWidth = this.$el.offsetWidth - 20;
  },
  watch: {
    sidebarWidth() {
      this.searchBarWidth = this.$el.offsetWidth - 20;
    },
    userLayouts: {
      handler(newLayouts) {
        this.sourceData = newLayouts;
        // Force reactivity by creating a new array reference
        this.$forceUpdate();
      },
      deep: true,
      immediate: true
    },
  },
  computed: {
    currentTab() {
      return store.getters["grid/currentTab"] ? store.getters["grid/currentTab"] : {};
    },
    tabs() {
      return store.state.grid.tabs;
    },
    tabsQty() {
      return Object.keys(this.tabs).length;
    },
    isConfigModeOn() {
      return store.state.psimConfig.configModeStatus;
    },
    userLayouts() {
      return store.state.layouts.userLayouts.map((l) => ({
        id: l.id,
        name: l.name,
        grid: l.data,
        isCard: l.is_card,
        distance: l.distance,
      }));
    },
    sidebarMinWidthReached() {
      if (this.forceWideLayout) return false;
      return store.getters["grid/sidebarMinWidthReached"];
    },
    sidebarWidth() {
      return store.state.grid.sidebarWidth;
    },
    items() {
      return this.sourceData.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },
    isToolbarsCollapsed() {
      if (this.ignoreCollapse) return false;
      return store.state.psimConfig.isToolbarsCollapsed;
    },
  },
  methods: {
    onUpdateStatus(value) {
      this.$emit("widget-collapsed", {
        id: this.id,
        property: "isOpen",
        location: this.location,
        value,
      });
    },
    checkSelectedLayout(idLayout) {
      if (this.parent === "sidebar") {
        return this.currentTab.layout === idLayout;
      }
      return this.selectedLayout == idLayout;
    },
    setLayout(layout) {
      if (this.parent === "sidebar") {
        if (this.tabsQty === 0) {
          store.dispatch("grid/addNewTab");
          store.dispatch("grid/setActiveTab", Object.keys(this.tabs)[0]);
        }
        try {
          const currentLayoutId = this.currentTab.layout;
          store.dispatch("grid/setLayout", layout.id).catch((error) => {
            console.error("Error al cambiar layout:", error);
            // Si hay error, volver al layout anterior
            store.dispatch("grid/setLayout", currentLayoutId);
          });
        } catch (error) {
          console.error("Error al sincronizar estado antes de cambiar layout:", error);
        }
      } else {
        this.$emit("layout-selected", layout.id);
      }
    },
    onSearch(value) {
      if (value.length < 1) return;

      const search = value.toLowerCase();

      this.sourceData = this.userLayouts.filter((layout) =>
        layout.name.toLowerCase().includes(search)
      );

      this.currentPage = 1;
    },
    toggleSearchBar(forceValue) {
      if (forceValue === true) {
        this.openSearchBar();
        return;
      }
      if (forceValue === false) {
        this.closeSearchBar();
        return;
      }

      if (this.searchBarEnabled) {
        this.closeSearchBar();
      } else {
        this.openSearchBar();
      }
    },

    openSearchBar() {
      if (this.searchBarEnabled) return;
      this.searchBarEnabled = true;
      this.isCollapseOpen = true;

      this.$nextTick(() => {
        if (this.$refs.layoutsSearchBar?.focusInput) {
          this.$refs.layoutsSearchBar.focusInput();
        }
      });
    },

    closeSearchBar() {
      if (!this.searchBarEnabled) return;
      this.searchBarEnabled = false;
      this.resetSearchBar();
    },

    resetSearchBar() {
      this.sourceData = this.userLayouts;
      this.currentPage = 1;
    },
    updateToolbarsStatus() {
      store.commit(
        "psimConfig/UPDATE_TOOLBARS_COLLAPSED",
        !store.state.psimConfig.isToolbarsCollapsed
      );

      this.isCollapseOpen = true;
    },
    increaseWidth() {
      this.hoverWidth = "22px";
    },
    resetWidth() {
      this.hoverWidth = "16px";
    },
  },
};
</script>

<style lang="scss" scoped>
.search-icon-config {
  margin-bottom: 1rem;
}
</style>
